import Eyebrow from "@/components/Eyebrow";
import PillarStrip from "@/components/PillarStrip";
import CountryClothWeave from "@/components/CountryClothWeave";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";

export default function About() {
  return (
    <>
      <SEO title="About" description="HOFA — a humanitarian nonprofit founded in 2015 in Mango Town, Brewerville, Liberia, with a USA office in Essex Junction, Vermont." path="/about" />
      <section
        className="text-white pt-24 pb-20"
        style={{ background: "linear-gradient(160deg,#123B23 0%,#1F5C38 78%)" }}
      >
        <div className="max-w-[1140px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-[1.2fr_.8fr] gap-14 items-center">
          <div>
            <Eyebrow tone="mango">About HOFA</Eyebrow>
            <h1 className="font-display font-bold text-white text-[clamp(38px,5vw,60px)] mb-5">
              Ten years of weaving hope in Liberia.
            </h1>
            <p className="text-white/85 text-[18px] max-w-[58ch]">
              Founded in 2015 in Mango Town, Brewerville, HOFA has grown into a community-led humanitarian nonprofit serving families across District 17 — with sister operations in Essex Junction, Vermont.
            </p>
          </div>
          <CountryClothWeave />
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-[1140px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-[1.2fr_.8fr] gap-14 items-start">
          <div>
            <Eyebrow>Our mission</Eyebrow>
            <h2 className="text-[clamp(30px,4vw,44px)] mb-6 max-w-[24ch]">
              We lead and inspire hope for the betterment of families and communities.
            </h2>
            <p className="mb-4 text-ink/85">
              We organize, support, participate, collaborate, and partner across five pillars — Education, Research, Social Engagement, Economic Empowerment, and Health Services — to build lasting, community-owned change.
            </p>
            <p className="mb-4 text-ink/85">
              Like Liberian country cloth, our strength comes from weaving many strips into one fabric. Each ERSEH pillar is a thread; together they hold a whole community.
            </p>
            <p className="text-ink/85">
              Every HOFA program is designed, staffed, and delivered with the people it serves — from classroom literacy in Mango Town to health outreach across District 17.
            </p>
          </div>
          <div className="bg-white border border-ink/10 rounded-[14px] p-8">
            <h3 className="text-xl font-display mb-4">Core values</h3>
            <ul className="list-none p-0">
              {[
                { c: "#F2A93B", t: "Teamwork", d: "We build together — staff, volunteers, and community." },
                { c: "#31517E", t: "Excellence", d: "Every program earns its impact." },
                { c: "#C05A3A", t: "Acceptance", d: "All are welcome, all are honored." },
                { c: "#1F5C38", t: "Commitment", d: "Long-term work, patient outcomes." },
                { c: "#A62639", t: "Honesty", d: "Transparent with communities and partners." },
              ].map((v, i, arr) => (
                <li key={v.t} className={`flex gap-3 py-3 ${i < arr.length - 1 ? "border-b border-dashed border-ink/15" : ""}`}>
                  <i className="mt-1.5 block w-2.5 h-2.5 rounded-[3px] shrink-0" style={{ background: v.c }} />
                  <div>
                    <div className="font-semibold">{v.t}</div>
                    <div className="text-sm text-ink/70">{v.d}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-ink text-paper py-24">
        <div className="max-w-[1140px] mx-auto px-6">
          <Eyebrow tone="mango">The ERSEH framework</Eyebrow>
          <h2 className="text-[clamp(30px,4vw,44px)] mb-3 text-white">Five pillars, woven together.</h2>
          <p className="text-white/70 max-w-[62ch] mb-12">
            Tap or hover any strip to see what each thread carries in our community work.
          </p>
          <PillarStrip />
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-[1140px] mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="rounded-[14px] border border-ink/10 bg-white p-8">
            <span className="text-xs uppercase tracking-[0.14em] font-bold text-palm">Liberia Headquarters</span>
            <h3 className="font-display text-2xl mt-2 mb-3">Mango Town, Brewerville</h3>
            <p className="text-ink/80 mb-2">Montserrado County · District 17</p>
            <p className="text-ink/70 text-sm">+231 770 758 626 · +231 77 659 5045</p>
          </div>
          <div className="rounded-[14px] border border-ink/10 bg-white p-8">
            <span className="text-xs uppercase tracking-[0.14em] font-bold text-clay">USA Office</span>
            <h3 className="font-display text-2xl mt-2 mb-3">Essex Junction, Vermont</h3>
            <p className="text-ink/80 mb-2">United States of America</p>
            <p className="text-ink/70 text-sm">+1 603 728 9072</p>
          </div>
        </div>
        <div className="max-w-[1140px] mx-auto px-6 mt-10">
          <Link to="/get-involved" className="inline-block bg-palm text-white font-bold rounded-full px-7 py-3.5 no-underline hover:-translate-y-0.5 hover:shadow-xl transition" data-testid="about-get-involved-btn">
            Support the work →
          </Link>
        </div>
      </section>
    </>
  );
}
